/**
 * ServerRetryModal — Confirmation du retry du script setup (admin).
 *
 * Body attendu : `{ reason: string (10..500) }`.
 *
 * @spec specs/app/admin/dedicated-servers.md §6.7
 */

import { Alert, Input, Modal } from 'antd';
import { RotateCcw } from 'lucide-react';
import { useState, type FC } from 'react';
import type { DedicatedServer } from '@/types/dedicatedServer';
import { useDedicatedServerActions } from '../hooks/useDedicatedServerActions';

interface ServerRetryModalProps {
  server: DedicatedServer;
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

const MIN_REASON = 10;
const MAX_REASON = 500;

const ServerRetryModal: FC<ServerRetryModalProps> = ({ server, open, onClose, onSuccess }) => {
  const [reason, setReason] = useState('');
  const { retry, isRetrying } = useDedicatedServerActions(server.id);

  const handleOk = () => {
    if (reason.length < MIN_REASON) return;
    retry(reason, {
      onSuccess: () => {
        setReason('');
        onClose();
        onSuccess?.();
      },
    });
  };

  const handleCancel = () => {
    setReason('');
    onClose();
  };

  const reasonTooShort = reason.length > 0 && reason.length < MIN_REASON;

  return (
    <Modal
      title={
        <span className="inline-flex items-center gap-2">
          <RotateCcw className="h-4 w-4 text-blue-500" />
          Relancer le setup
        </span>
      }
      open={open}
      onOk={handleOk}
      onCancel={handleCancel}
      okText="Relancer"
      cancelText="Annuler"
      okButtonProps={{
        type: 'primary',
        loading: isRetrying,
        disabled: reason.length < MIN_REASON,
      }}
      destroyOnClose
    >
      <div className="space-y-3">
        <p className="text-sm text-slate-600 dark:text-slate-300">
          Le script de setup va être relancé depuis le stage échoué. Le serveur passera en statut{' '}
          <strong>Installation</strong> le temps de la reprise.
        </p>
        <Alert
          type="info"
          showIcon
          message="Action réversible"
          description="Aucune destruction. En cas d'échec, vous pourrez essayer un rebuild complet."
        />
        <div>
          <label className="mb-1 block text-xs font-medium text-slate-700 dark:text-slate-300">
            Raison (obligatoire, {MIN_REASON}–{MAX_REASON} caractères)
          </label>
          <Input.TextArea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Ex: ticket #1234 — échec stage docker, relance après correctif"
            maxLength={MAX_REASON}
            showCount
            rows={3}
            status={reasonTooShort ? 'error' : undefined}
          />
          {reasonTooShort && (
            <p className="mt-1 text-xs text-red-500">
              Minimum {MIN_REASON} caractères.
            </p>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default ServerRetryModal;
